import { BtlTexts, BtlWordKey } from 'api/video';
import { LogoPlace } from 'types/adEditorTypes';
import { logoPlaceLabel } from 'components/logo-place/LogoPlaceButtons';

import { TEMPLATE_STYLES } from './mockData';
import { VideoTemplateStyle } from './types';

/**
 * SYM-6353. The three words on a Bring to Life clip, and what the pop-up says
 * about each one.
 *
 * Every word starts as the image ad's own (the suggestion the server sends)
 * and can be changed or emptied. The labels are templates v2's (D20, Mark's
 * wording): a green "Automatic (from ...)" while the word follows its source,
 * "Custom" once changed, "Hidden" once emptied, and a "Reset the ..." link that
 * names the value it puts back.
 */
export interface BtlWordField {
  key: BtlWordKey;
  /** The label left of the box. */
  label: string;
  /**
   * The most the box takes. Callout 16 (the image templates' badge,
   * TemplateValueControls MAX_LENGTHS.banner_title), text 45 (Steven, 27 Sep
   * 2026; it was 30). Location has no
   * limit the customer sees -- a long one is shrunk to fit, never cut -- so it
   * carries only the server's ceiling and shows no counter.
   */
  cap: number;
  showCounter: boolean;
  /** The green chip while the word is the suggestion. */
  automatic: string;
  /** The link that puts the suggestion back, before "(value)". */
  reset: string;
}

export const BTL_WORD_FIELDS: BtlWordField[] = [
  {
    key: 'callout',
    label: 'Callout',
    cap: 16,
    showCounter: true,
    automatic: 'Automatic (from Brand)',
    reset: 'Reset the brand callout',
  },
  {
    key: 'title',
    label: 'Text',
    cap: 45,
    showCounter: true,
    automatic: 'Automatic (from Targeting)',
    reset: 'Reset the campaign title',
  },
  {
    key: 'location',
    label: 'Location',
    cap: 200,
    showCounter: false,
    automatic: 'Automatic (from Targeting)',
    reset: 'Reset the targeting location',
  },
];

/**
 * What the customer did to each word. A key that is not here was left alone
 * (or put back with the reset link) and follows the suggestion.
 */
export type BtlWordEdits = Partial<Record<BtlWordKey, string>>;

export type BtlSuggestions = Record<BtlWordKey, string> | null;

export type BtlWordState = 'automatic' | 'custom' | 'hidden';

/** What the box shows: the customer's own word, else the suggestion. */
export const shownWord = (
  key: BtlWordKey,
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): string => edits[key] ?? suggestions?.[key] ?? '';

/**
 * Which label a word gets.
 *
 * A word typed back to exactly the suggestion is automatic again: it is the
 * suggestion, and calling it "Custom" would say the customer changed something
 * they did not. For the same reason an empty box is automatic when the
 * suggestion itself is empty (a brand without a callout), not "Hidden".
 * Before the suggestions are known, any word the customer typed is theirs.
 */
export const wordState = (
  key: BtlWordKey,
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): BtlWordState => {
  const edit = edits[key];
  if (edit === undefined) return 'automatic';
  if (suggestions && edit.trim() === (suggestions[key] ?? '').trim()) {
    return 'automatic';
  }
  return edit.trim() === '' ? 'hidden' : 'custom';
};

/**
 * SYM-6353, "option A" (Steven, 27 Sep 2026). A suggestion longer than its
 * cap (a long job title, a long brand callout) is shown whole and used as it
 * is while it is left alone: it is the ad's own word and the server draws it.
 * Once the customer edits it, it is THEIR word and must fit the cap: deleting
 * is always allowed, and Generate waits until it fits or the word is reset.
 */
export const wordTooLong = (
  field: Pick<BtlWordField, 'key' | 'cap'>,
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): boolean =>
  suggestions !== null &&
  wordState(field.key, edits, suggestions) === 'custom' &&
  shownWord(field.key, edits, suggestions).length > field.cap;

/** True while any word the customer edited is still over its cap. */
export const anyWordTooLong = (
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): boolean =>
  BTL_WORD_FIELDS.some((field) => wordTooLong(field, edits, suggestions));

/**
 * The `texts` sent to the server: ONLY the words the customer changed or
 * emptied (the contract, SYM-6353 plan section 1). A word left as suggested is
 * left out, so the server uses its own suggestion. Emptied words are sent as
 * "", which the server reads as "hide this line". Returns null when there is
 * nothing to send.
 */
export const textsToSend = (
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): BtlTexts | null => {
  const texts: BtlTexts = {};
  BTL_WORD_FIELDS.forEach(({ key }) => {
    if (wordState(key, edits, suggestions) === 'automatic') return;
    texts[key] = (edits[key] ?? '').trim();
  });
  return Object.keys(texts).length ? texts : null;
};

/**
 * The words for the PREVIEW request: the same as textsToSend, minus any word
 * the customer edited that is still over its cap. The server's preview holds
 * words to the same ceiling and would refuse the whole request, leaving a
 * "not your latest change" note whose Try again can never succeed. Left out,
 * that word previews as its suggestion, and the field's own "Shorten to ..."
 * line is the one warning (Generate is held until it fits).
 */
export const previewTexts = (
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): BtlTexts | null => {
  const texts = textsToSend(edits, suggestions);
  if (!texts) return null;
  BTL_WORD_FIELDS.forEach((field) => {
    if (wordTooLong(field, edits, suggestions)) delete texts[field.key];
  });
  return Object.keys(texts).length ? texts : null;
};

/**
 * True when no word would be drawn at all, which the server refuses ("Add at
 * least one line of text."). Only decided once the suggestions are known, or
 * once the customer has emptied all three by hand: before that, an empty box
 * may only mean the suggestion has not arrived yet.
 */
export const allWordsEmpty = (
  edits: BtlWordEdits,
  suggestions: BtlSuggestions
): boolean => {
  const known = (key: BtlWordKey) =>
    suggestions !== null || edits[key] !== undefined;
  return BTL_WORD_FIELDS.every(
    ({ key }) => known(key) && shownWord(key, edits, suggestions).trim() === ''
  );
};

/** The line in the footer: "Dynamic · logo top right · 40-60 seconds". */
export const btlSummary = (
  look: VideoTemplateStyle,
  logo: LogoPlace
): string => {
  const lookName = TEMPLATE_STYLES.find((s) => s.id === look)?.name ?? look;
  const logoWords =
    logo === 'none' ? 'no logo' : `logo ${logoPlaceLabel(logo).toLowerCase()}`;
  return `${lookName} · ${logoWords} · 40-60 seconds`;
};

/**
 * Everything the customer chose on the Bring to Life screen. Carried whole
 * through the trip to the crop tool and back, so nothing has to be chosen twice.
 */
export interface BtlChoices {
  /** How the picture should move; may be empty. */
  instructions: string;
  look: VideoTemplateStyle;
  logo: LogoPlace;
  /** The words as the customer left them (for coming back to the screen). */
  edits: BtlWordEdits;
  /** What goes to the server: only the words changed or emptied. */
  texts: BtlTexts | null;
}

export const DEFAULT_BTL_LOOK: VideoTemplateStyle = 'dynamic';
export const DEFAULT_BTL_LOGO: LogoPlace = 'top-right';

/**
 * SYM-6353. What the Bring to Life request adds to `video` for the drawing
 * over the clip: the ad it comes from, the look and the logo ALWAYS, and the
 * words ONLY where the customer changed or emptied one (absent = the
 * suggestion; see BtlTexts).
 */
export const btlOverlayFields = (
  adVariantId: number | null | undefined,
  choices: Pick<BtlChoices, 'look' | 'logo' | 'texts'>
): {
  ad_variant_id?: number;
  render_style: VideoTemplateStyle;
  logo_position: LogoPlace;
  texts?: BtlTexts;
} => ({
  ...(typeof adVariantId === 'number' ? { ad_variant_id: adVariantId } : {}),
  render_style: choices.look,
  logo_position: choices.logo,
  ...(choices.texts && Object.keys(choices.texts).length
    ? { texts: choices.texts }
    : {}),
});

/** What the server refused about the words, read from a 422's error bag. */
export interface BtlWordRefusal {
  /** Per word, the server's own sentence (e.g. a callout over 16). */
  words: Partial<Record<BtlWordKey, string>>;
  /** About the words as a whole: all three empty. */
  all: string | null;
}

const firstMessage = (bag: Record<string, unknown>, key: string) => {
  const value = bag[key];
  const one = Array.isArray(value) ? value[0] : value;
  return typeof one === 'string' && one ? one : null;
};

/**
 * SYM-6353. The server's refusal of the words, or null for any other answer.
 * Read from `texts` / `texts.<word>`, with or without the `video.` in front
 * (Laravel names a nested field by its full path).
 */
export const readWordRefusal = (errors: unknown): BtlWordRefusal | null => {
  if (!errors || typeof errors !== 'object') return null;
  const bag = errors as Record<string, unknown>;
  const words: Partial<Record<BtlWordKey, string>> = {};
  BTL_WORD_FIELDS.forEach(({ key }) => {
    const message =
      firstMessage(bag, `video.texts.${key}`) ??
      firstMessage(bag, `texts.${key}`);
    if (message) words[key] = message;
  });
  const all = firstMessage(bag, 'video.texts') ?? firstMessage(bag, 'texts');
  return Object.keys(words).length || all ? { words, all } : null;
};
